/**
 * Life4Earth V2 — React hook for the caller's open mini-project tasks
 * across every MP the caller can read.
 *
 * DEV-1257. Powers the "Task" rows in the Action Center inbox. Mirrors
 * useApiRoleRhythmInstances exactly: in-memory queryCache key, refetch
 * exposed for after-mutation refresh, CACHE_UPDATE_EVENT subscription so
 * an external invalidate triggers a reload.
 *
 * Backend: GET /api/action-items/mini-project-tasks
 */

import { useState, useEffect, useCallback, useRef } from "react";
import {
  fetchMyOpenMiniProjectTasks,
  type MyMiniProjectTask,
} from "./miniProjectTasks";
import { fetchForKey, CACHE_UPDATE_EVENT } from "./queryCache";

export interface UseApiMyMiniProjectTasksResult {
  tasks: MyMiniProjectTask[];
  loading: boolean;
  error: string | null;
  source: "api" | "empty" | "loading";
  refetch: () => void;
}

const CACHE_KEY = "miniProjectTasks:mine:all";

export function useApiMyMiniProjectTasks(): UseApiMyMiniProjectTasksResult {
  const mountedRef = useRef(true);
  const [data, setData] = useState<UseApiMyMiniProjectTasksResult>({
    tasks: [],
    loading: true,
    error: null,
    source: "loading",
    refetch: () => {},
  });

  const refetch = useCallback(async () => {
    if (!mountedRef.current) return;
    try {
      const rows = await fetchForKey<MyMiniProjectTask[]>(
        CACHE_KEY,
        fetchMyOpenMiniProjectTasks,
        { force: true },
      );
      if (!mountedRef.current) return;
      setData({
        tasks: rows,
        loading: false,
        error: null,
        source: rows.length > 0 ? "api" : "empty",
        refetch,
      });
    } catch (err) {
      if (!mountedRef.current) return;
      setData((prev) => ({
        ...prev,
        loading: false,
        error: err instanceof Error ? err.message : String(err),
        source: "empty",
        refetch,
      }));
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void refetch();

    const onCacheUpdate = (e: Event) => {
      const detail = (e as CustomEvent<{ key: string }>).detail;
      if (detail?.key === CACHE_KEY) void refetch();
    };
    window.addEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);

    return () => {
      mountedRef.current = false;
      window.removeEventListener(CACHE_UPDATE_EVENT, onCacheUpdate);
    };
  }, [refetch]);

  return data;
}
